/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Loading from "components/NewLoading";
import Icon from "@mui/material/Icon";
import brandWhite from "assets/images/logo-without-modules.png";
import { actions } from "store/reducers/qrCodeClockIn";
import Webcam from "react-webcam";
import SignaturePad from "react-signature-canvas";
import IdleTimeout from "../../helpers/IdleTimeout";

const videoConstraints = {
  width: 300,
  height: 300,
  facingMode: "user",
};
const Style = styled.div`
  .icon {
    color: white;
    background: #2595d1;
    border-radius: 50%;
    font-weight: bold;
    font-size: 80px !important;
    padding: 6px;
    height: auto;
    width: auto;
  }

  .sigPad {
    width: 400px !important;
    height: 300px !important;
    border: 1px solid #ccc;
  }

  .current-time {
    text-align: center;
  }

  .end-clock-message {
    font-size: 16px;

    .end-clock-status {
      color: #00a7e2;
      text-transform: uppercase;
      font-weight: bold;
    }
  }

  .ok-button {
    background-color: #00a7e2;
    width: 50%;
    min-wdith: 200px;
    max-wdith: 80%;
  }

  .clock-button {
    background-color: #00a7e2;
    width: 200px;
  }

  .clock-info {
    text-align: center;

    .MuiTypography-root,
    .clock-info_clock {
      font-size: 17px;
    }

    .clock-info_clock {
      color: #00a7e2;
      font-weight: bold;
    }

    .clock-info_dot {
      margin: 0px auto;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #ed1b24;
    }

    .clock-info_dot-text {
      color: #ed1b24;
      font-weight: bold;
    }

    &.clocked {
      .clock-info_dot {
        background: #0ed145;
      }
      .clock-info_dot-text {
        color: #0ed145;
      }
    }
  }

  .log-out-button {
    border-radius: 0;
    border-top-right-radius: 0.6rem;
  }
`;
const getTime = (timezone, time = null) => {
  if (timezone && timezone.value) {
    return time ? momentTimezone.tz(time, timezone.value) : momentTimezone.tz(timezone.value);
  }
  return moment(time);
};

function LoggedIn() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    isClockingIn,
    isClockingOut,
    clockingInError,
    clockingOutError,
    qrCode,
    user,
    shift,
    isCheckingIfClockedIn,
    checkingIfClockedInError,
  } = useSelector((state) => state.qrCodeClockIn);
  const currentTimeIntervalRef = useRef();
  const [currentTime, setCurrentTime] = useState(getTime(qrCode?.timezone));
  const [isFinished, setIsFinished] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
  const [openSignature, setOpenSignature] = useState(false);
  const [signPad, setSignPad] = useState({});
  const [profileImage, setProfileImage] = useState(undefined);
  const webcamRef = useRef(null);

  const lastClock = useMemo(() => {
    if (shift) {
      return shift.clocks[shift.clocks.length - 1];
    }
    return null;
  }, [shift]);

  const lastClockIn = useMemo(() => {
    if (shift) {
      const clockIns = shift.clocks.filter((clockIn) => clockIn.type.name === "ON");
      return clockIns[clockIns.length - 1];
    }
    return null;
  }, [shift]);

  const isClockedIn = useMemo(() => !!lastClockIn, [lastClockIn]);

  const shiftDuration = useMemo(() => {
    if (shift && qrCode) {
      const creationTime = getTime(qrCode?.timezone, lastClockIn.time);
      const duration = moment.duration(currentTime.diff(creationTime));
      return ["hours", "minutes", "seconds"]
        .map((v) => duration.get(v))
        .map((v) => (v.toString().length > 1 ? v : `0${v}`))
        .join(":");
    }
    return null;
  }, [qrCode, currentTime, lastClockIn, shift]);

  const logOut = useCallback(
    (redirect = true) => {
      dispatch(actions.requestClockInLogout(redirect));
    },
    [dispatch]
  );

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot({ screenshotQuality: 0.2 });
    setProfileImage(imageSrc);
    if (qrCode.signShift) {
      setOpenSignature(true);
      setOpenCamera(false);
    } else {
      dispatch(
        actions.requestClockOut(() => {
          setOpenCamera(false);
          setTimeout(logOut, 20000);
          setIsFinished(true);
        }, imageSrc)
      );
    }
  }, [webcamRef]);
  const getSignature = useCallback(() => {
    const signImg = signPad.getTrimmedCanvas().toDataURL("image/png");
    dispatch(
      actions.requestClockOut(
        () => {
          setIsFinished(true);
          setOpenSignature(false);
          setTimeout(logOut, 20000);
        },
        profileImage,
        signImg
      )
    );
  }, [signPad]);

  const clockIn = useCallback(() => {
    dispatch(
      actions.requestClockIn(() => {
        setIsFinished(true);
        setTimeout(logOut, 20000);
      })
    );
  }, [dispatch]);

  const clockOut = useCallback(() => {
    if (qrCode.profileImage) {
      setOpenCamera(true);
    } else if (qrCode.signShift) {
      setOpenSignature(true);
    } else {
      dispatch(
        actions.requestClockOut(() => {
          setTimeout(logOut, 20000);
          setIsFinished(true);
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.requestCheckIfClockedIn());
  }, [dispatch]);

  useEffect(() => {
    currentTimeIntervalRef.current = setInterval(() => {
      setCurrentTime(getTime(qrCode?.timezone));
    }, 1000);
    return () => clearInterval(currentTimeIntervalRef.current);
  }, [setCurrentTime, currentTimeIntervalRef, qrCode]);

  useEffect(() => {
    if (clockingInError) {
      toast.error(clockingInError);
    }
  }, [clockingInError]);

  useEffect(() => {
    if (clockingOutError) {
      toast.error(clockingOutError);
    }
  }, [clockingOutError]);

  if (isCheckingIfClockedIn) {
    return (
      <div style={{ textAlign: "center" }}>
        <Loading />
      </div>
    );
  }

  if (checkingIfClockedInError) {
    return (
      <Card>
        <MDBox py={3} style={{ textAlign: "center" }}>
          {checkingIfClockedInError}
        </MDBox>
      </Card>
    );
  }

  if (openCamera) {
    return (
      <Style>
        <Card>
          <CardContent>
            <MDBox pt={3} style={{ textAlign: "center" }}>
              <MDTypography style={{ fontSize: 17, textAlign: "center", color: "#00a7e2" }}>
                Please send your profile image
              </MDTypography>
            </MDBox>
            <MDBox pt={3} style={{ textAlign: "center" }}>
              <Webcam
                audio={false}
                height={200}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.5}
                width={200}
                videoConstraints={videoConstraints}
              />
            </MDBox>
            <MDBox pb={3} style={{ textAlign: "center" }}>
              <MDButton
                className="log-out-button"
                color="dark"
                aria-label="Capture photo"
                onClick={capture}
              >
                <Icon>camera</Icon> &nbsp; Capture photo
              </MDButton>
            </MDBox>
          </CardContent>
        </Card>
      </Style>
    );
  }

  if (openSignature) {
    return (
      <Style>
        <Card>
          <CardContent>
            <MDBox pt={3} style={{ textAlign: "center" }}>
              <MDTypography style={{ fontSize: 17, textAlign: "center", color: "#00a7e2" }}>
                Please sign your shift
              </MDTypography>
            </MDBox>
            <MDBox pt={3} style={{ textAlign: "center" }}>
              <SignaturePad
                canvasProps={{ className: "sigPad", width: 400, height: 300 }}
                ref={(ref) => setSignPad(ref)}
              />
            </MDBox>
            <MDBox pb={3} style={{ textAlign: "center" }}>
              <MDButton
                className="log-out-button"
                color="default"
                aria-label="Clear"
                onClick={() => signPad.clear()}
              >
                <Icon>clear</Icon> &nbsp; Clear
              </MDButton>
              <MDButton
                className="log-out-button"
                color="dark"
                aria-label="Sign"
                onClick={getSignature}
              >
                <Icon>article</Icon> &nbsp; Sign
              </MDButton>
            </MDBox>
          </CardContent>
        </Card>
      </Style>
    );
  }

  if (isFinished) {
    return (
      <Style>
        <Card>
          <CardContent>
            <MDBox mb={3}>
              <Grid container alignItems="center" flexDirection="column">
                <Grid item>
                  <MDBox pt={3}>
                    <img height={70} src={brandWhite} alt="logo" />
                  </MDBox>
                </Grid>

                <Grid item>
                  <MDBox mb={4}>
                    <Icon className="icon">check</Icon>
                  </MDBox>
                </Grid>

                <Grid item>
                  <MDBox mb={3}>
                    <MDTypography variant="h2" fontWeight="medium" textTransform="capitalize">
                      {t("Success")}!
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item>
                  <MDBox mb={3}>
                    <MDTypography className="end-clock-message">
                      {lastClock?.type?.name === "ON"
                        ? t("pre_success_clock_in")
                        : t("pre_success_clock_out")}{" "}
                      <span className="end-clock-status">
                        {lastClock?.type?.name === "ON" ? t("IN_status") : t("OUT_status")}
                      </span>
                    </MDTypography>

                    <MDTypography className="current-time">
                      {currentTime.format("M/D/YYYY")}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item>
                  <MDBox mb={3}>
                    <MDTypography>{currentTime.format("kk:mm:ss A")}</MDTypography>
                  </MDBox>
                </Grid>

                <MDButton color="info" className="ok-button" onClick={logOut}>
                  {t("OK")}
                </MDButton>
              </Grid>
            </MDBox>
          </CardContent>
        </Card>
      </Style>
    );
  }

  return (
    <Style>
      <IdleTimeout time={20} onTimeout={logOut}>
        <Card>
          <CardContent>
            <MDBox style={{ marginTop: -8, marginRight: -24 }}>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <MDButton
                    className="log-out-button"
                    color="dark"
                    aria-label="Log out"
                    onClick={() => logOut(false)}
                    disabled={isClockingIn || isClockingOut}
                  >
                    <Icon>logout</Icon>
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mb={3}>
              <Grid container justifyContent="center">
                <Grid item>
                  <img height={70} src={brandWhite} alt="logo" />
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mb={3}>
              <Grid container justifyContent="center">
                <Grid item>
                  <MDTypography style={{ fontSize: 17, textAlign: "center" }}>
                    {user.firstName} {user.lastName}
                  </MDTypography>

                  <MDTypography style={{ fontSize: 17, textAlign: "center", color: "#00a7e2" }}>
                    {t("Subaccount")}: {qrCode.project}
                  </MDTypography>
                  <MDTypography style={{ fontSize: 14, textAlign: "center" }}>
                    {qrCode.description}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mb={3}>
              <Grid container justifyContent="center">
                <Grid item>
                  <MDTypography style={{ textTransform: "capitalize" }}>
                    {currentTime.format("MMMM D, YYYY")}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mb={8}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={4}>
                  <div className="clock-info">
                    <div className="clock-info_clock">
                      {isClockedIn
                        ? getTime(qrCode?.timezone, lastClockIn.time).format("kk:mm A")
                        : currentTime.format("kk:mm A")}
                    </div>
                    <MDTypography>{t(isClockedIn ? "START TIME" : "CURRENT TIME")}</MDTypography>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  <div className={`clock-info ${isClockedIn ? "clocked" : ""}`}>
                    <div className="clock-info_dot" />
                    <div className="clock-info_dot-text">{t(isClockedIn ? "IN" : "OUT")}</div>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  <div className="clock-info">
                    <div className="clock-info_clock">{shift ? shiftDuration : "00:00:00"}</div>
                    <MDTypography>{t("DURATION")}</MDTypography>
                  </div>
                </Grid>
              </Grid>
            </MDBox>

            <Grid container justifyContent="center">
              <Grid item>
                <MDButton
                  color="info"
                  className="clock-button"
                  onClick={isClockedIn ? clockOut : clockIn}
                  disabled={isClockingIn || isClockingOut}
                >
                  {t(
                    isClockedIn
                      ? isClockingOut
                        ? "Clocking out"
                        : "Clock out"
                      : isClockingIn
                      ? "Clocking In"
                      : "Clock in"
                  )}
                </MDButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </IdleTimeout>
    </Style>
  );
}

export default LoggedIn;
